/**
 * capabilityAction.ts — INSPECT → CONFIGURE → PREVIEW → REVIEW → APPLY →
 * READBACK → DIAGNOSTICS, derived entirely from a published descriptor.
 *
 * This is the control loop the whole workbench runs on. A multidisciplinary
 * P&ID edit, an electrical change, a panel placement, an ATEX assessment and a
 * safety edit are the SAME loop with a different descriptor, so there is exactly
 * one implementation of the loop and no per-discipline copy of it.
 *
 * NOTHING HERE IS ENGINE VOCABULARY. There is no operation list, no field list,
 * no enum, no required-by-domain rule. Every value below is read from the
 * descriptor and its transport at the moment it is used, which is what makes
 * the unseen-capability property real rather than aspirational: an Engine
 * capability this repository has never heard of reaches the loop by the same
 * code path as a known one, with no edit here.
 *
 * THE TWO RULES THAT MATTER MOST
 * ------------------------------
 *
 * 1. FAIL CLOSED ON ACCEPTANCE. Whether a result may be staged is read from the
 *    published `resultSchema`, never inferred. If the Engine published no
 *    acceptance flag, the client reports the result verbatim and offers no
 *    stage. Guessing "it looked fine" is how a refusal becomes an applied edit.
 *
 * 2. THE BROWSER NEVER DECIDES VALIDITY. A form is a set of the Engine's own
 *    bounded fields. Whether the payload is acceptable, whether two things may
 *    be connected, whether a fit is feasible: the Engine answers, and this file
 *    has no function that could answer differently. There is deliberately no
 *    validation here beyond JSON well-formedness of an undeclared payload.
 */

import type { AuthoringRequest, AuthoringResult } from "../types/contract";
import type {
  CapabilityDescriptor,
  CapabilityManifest,
  JsonSchemaFragment,
} from "./capabilityManifest";
import { requiresHumanReview } from "./capabilityManifest";
import {
  addressValue,
  resolveTransport,
  type ActionPhase,
  type TransportPlan,
  type TransportUnavailable,
} from "./capabilityTransport";

// ---------------------------------------------------------------------------
// Inspect
// ---------------------------------------------------------------------------

/** One field of a derived form, as the Engine published it. */
export interface ActionField {
  readonly name: string;
  /**
   * The Engine's own `type`, or `UNPUBLISHED_TYPE` when it published none.
   *
   * Named rather than given a plausible engineering word, because an absent
   * schema is a fact about the PUBLICATION rather than an indeterminate
   * quantity — and this repository must not hold any engineering vocabulary.
   */
  readonly type: string;
  readonly description: string;
  readonly required: boolean;
  readonly readOnly: boolean;
  /** The Engine's own closed vocabulary for this field, when it published one. */
  readonly options: readonly string[];
  /** The Engine declares further members of this field. */
  readonly open: boolean;
  /** Object-typed: the browser offers a JSON editor and does not pretend. */
  readonly structured: boolean;
}

export interface ActionPlan {
  readonly descriptor: CapabilityDescriptor;
  readonly address: string;
  readonly transport: TransportPlan;
  readonly fields: readonly ActionField[];
  /**
   * Whether the published schema describes the whole payload.
   *
   * FALSE when the Engine's schema says the payload may carry further members
   * it did not describe. That is the honest case for reviewed plan operations,
   * whose payload the Engine validates in its own command module: the form can
   * offer only what was published, so the UI must say that rather than imply
   * the form is the whole contract.
   */
  readonly schemaComplete: boolean;
  /** Fields the Engine marked read-only. Rendered, never sent. */
  readonly derivedFields: readonly string[];
  /**
   * Properties the BROWSER supplies: the envelope address and its auxiliary
   * fields. Never offered as form controls, and always shown in the inspect
   * panel so the request stays reviewable.
   */
  readonly envelopeOwned: readonly string[];
  /** The Engine's own policy. This module never decides it. */
  readonly humanReview: boolean;
  readonly phases: readonly ActionPhase[];
  /** A caller-chosen correlation id; the Engine's, not this module's, authority. */
  readonly operationId: string;
}

export interface UnaddressableAction {
  readonly descriptor: CapabilityDescriptor;
  readonly unavailable: TransportUnavailable;
}

export type ActionResolution =
  | { readonly addressable: true; readonly plan: ActionPlan }
  | { readonly addressable: false; readonly unaddressable: UnaddressableAction };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function propertiesOf(schema: JsonSchemaFragment | undefined): Record<string, JsonSchemaFragment> {
  const raw = schema?.properties;
  return isRecord(raw) ? (raw as Record<string, JsonSchemaFragment>) : {};
}

/**
 * The closed vocabulary a schema publishes, from EITHER `enum` or `oneOf`.
 *
 * Both dialects are read because the Engine publishes both, and reading only one
 * is how a bounded vocabulary silently becomes a free-text box. For a SAFETY
 * vocabulary that is not a cosmetic failure: a determination field rendered as an
 * unconstrained text input invites exactly the value the drawing must never
 * accept, and the browser would have no way to recognise it.
 */
function stringOptions(schema: JsonSchemaFragment): readonly string[] {
  const options: string[] = [];
  if (Array.isArray(schema.enum)) {
    for (const value of schema.enum) if (typeof value === "string") options.push(value);
  }
  if (Array.isArray(schema.oneOf)) {
    for (const member of schema.oneOf) {
      if (!isRecord(member)) continue;
      if (typeof member.const === "string") options.push(member.const);
      else if (Array.isArray(member.enum) && member.enum.length === 1 && typeof member.enum[0] === "string") {
        options.push(member.enum[0] as string);
      }
    }
  }
  return [...new Set(options)];
}

/**
 * The `type` reported for a field whose schema the Engine left untyped.
 *
 * A distinct token, never a plausible-sounding guess like "string": a form that
 * offers a text box because it ASSUMED text is the browser inventing a
 * constraint, and an invented constraint is a value the Engine never agreed to.
 */
export const UNPUBLISHED_TYPE = "(unpublished)";

let operationCounter = 0;
/** Unique within this browsing session; the Engine remains the authority on scope. */
export function newCorrelationId(): string {
  operationCounter += 1;
  return `op-${Date.now().toString(36)}-${operationCounter.toString(36)}`;
}

/**
 * Derive the form from the Engine's own schema.
 *
 * Order is deterministic — required first, then alphabetical — so the same
 * manifest digest always renders the same form, and a reviewer comparing two
 * runs is comparing like with like.
 *
 * `envelopeOwned` names the properties the BROWSER supplies rather than the
 * user: the address that says which registered operation this is, and the
 * transport's own auxiliary fields. They are excluded from the form for the same
 * reason read-only fields are excluded — the browser sets them, and offering a
 * control for a value the user does not choose invites them to change it.
 * They are still shown in the INSPECT panel, because hiding them entirely would
 * make the request harder to review, not easier.
 */
export function deriveFields(descriptor: CapabilityDescriptor, envelopeOwned: readonly string[] = []): readonly ActionField[] {
  const properties = propertiesOf(descriptor.requestSchema);
  const required = new Set(descriptor.requestSchema.required ?? []);
  const readOnly = new Set(descriptor.presentation.readOnlyFields);
  const supplied = new Set(envelopeOwned);
  const names = Object.keys(properties).filter(name => !supplied.has(name)).sort();
  const fields: ActionField[] = names.map(name => {
    const schema = properties[name]!;
    const options = stringOptions(schema);
    const structured = schema.type === "object" || schema.type === "array" || isRecord(schema.properties) || isRecord(schema.items);
    return Object.freeze({
      name,
      type: typeof schema.type === "string" ? schema.type : UNPUBLISHED_TYPE,
      description: typeof schema.description === "string" ? schema.description : "",
      required: required.has(name),
      readOnly: readOnly.has(name),
      options: Object.freeze(options),
      // `additionalProperties: true` on the property, or no published bound, means
      // the Engine's vocabulary is not closed. Saying so is the difference
      // between a dropdown and a text box, and guessing wrong is a wrong answer.
      open: !options.length || schema.additionalProperties === true,
      structured,
    });
  });
  return Object.freeze([
    ...fields.filter(field => field.required),
    ...fields.filter(field => !field.required),
  ]);
}

/**
 * Inspect one capability: how it is addressed, what it takes, and whether a
 * human is required before it may be published.
 */
export function inspect(manifest: CapabilityManifest, descriptor: CapabilityDescriptor): ActionResolution {
  const transport = resolveTransport(manifest, descriptor);
  if (!transport.available) return { addressable: false, unaddressable: { descriptor, unavailable: transport.unavailable } };
  const { plan } = transport;
  // The plan envelope carries the address (`type`) and the caller's correlation
  // id as SIBLINGS of the payload, both declared by the transport rather than by
  // the capability. They are excluded from the form so the derived fields are the
  // capability's own parameters — the request the user is actually configuring.
  const envelopeOwned = Object.freeze([
    plan.addressField,
    ...plan.auxiliaryFields,
  ]);
  const fields = deriveFields(descriptor, envelopeOwned);
  return {
    addressable: true,
    plan: Object.freeze({
      descriptor,
      address: addressValue(descriptor, plan.addressField),
      transport: plan,
      fields,
      envelopeOwned,
      schemaComplete: descriptor.requestSchema.additionalProperties === false,
      derivedFields: Object.freeze([...descriptor.presentation.readOnlyFields].sort()),
      humanReview: requiresHumanReview(descriptor),
      phases: plan.phases,
      operationId: newCorrelationId(),
    }),
  };
}

/** Every capability the Engine published, in the order the workbench lists them. */
export function inspectAll(manifest: CapabilityManifest): readonly ActionResolution[] {
  return [...manifest.capabilities, ...manifest.projections].map(descriptor => inspect(manifest, descriptor));
}

// ---------------------------------------------------------------------------
// Configure
// ---------------------------------------------------------------------------

/** The values a form holds. Strings, always: the Engine parses, not this module. */
export type FieldValues = Readonly<Record<string, string>>;

export type ValueProblem =
  | { readonly field: string; readonly problem: "json"; readonly detail: string }
  | { readonly field: string; readonly problem: "enum"; readonly detail: string }
  | { readonly field: string; readonly problem: "number"; readonly detail: string };

/**
 * Check the values against the Engine's own schema — and ONLY that.
 *
 * The checks are the schema's own: a closed enumeration rejects a value outside
 * it, a number rejects a non-number, a structured field rejects text that is
 * not JSON. There is no check here that a value is engineering-valid, because
 * this browser has no authority to make that judgement and a browser that
 * guessed would be a second source of truth wearing a form.
 */
export function validateValues(plan: ActionPlan, values: FieldValues): readonly ValueProblem[] {
  const problems: ValueProblem[] = [];
  for (const field of plan.fields) {
    if (field.readOnly) continue;
    const raw = (values[field.name] ?? "").trim();
    if (raw === "") {
      if (field.required) {
        problems.push({ field: field.name, problem: "json", detail: `${field.name} is required by the Engine's schema.` });
      }
      continue;
    }
    if (!field.options.length && field.open === false) continue;
    if (field.options.length > 0 && !field.options.includes(raw)) {
      problems.push({
        field: field.name,
        problem: "enum",
        detail: `${field.name} must be one of the values the Engine published.`,
      });
      continue;
    }
    if (field.structured) {
      try { JSON.parse(raw); }
      catch { problems.push({ field: field.name, problem: "json", detail: `${field.name} must be valid JSON.` }); }
      continue;
    }
    if (field.type === "number" || field.type === "integer") {
      if (!Number.isFinite(Number(raw))) {
        problems.push({ field: field.name, problem: "number", detail: `${field.name} must be a number.` });
      }
    }
  }
  return problems;
}

/** Whether a field's value is complete enough to send. */
export function fieldComplete(field: ActionField, values: FieldValues): boolean {
  if (field.readOnly) return true;
  const raw = (values[field.name] ?? "").trim();
  return raw !== "" || !field.required;
}

function coerce(field: ActionField, raw: string): unknown {
  if (raw === "") return undefined;
  if (field.structured) {
    try { return JSON.parse(raw); } catch { return raw; }
  }
  if (field.type === "number" || field.type === "integer") {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : raw;
  }
  if (field.type === "boolean") return raw === "true";
  return raw;
}

/**
 * Build the payload object the descriptor's own schema describes.
 *
 * Read-only fields are dropped rather than sent: the Engine, not the browser,
 * decides what is writable, and a client that echoes a read-only field is
 * asserting a value it was told it may not set.
 */
export function buildPayload(plan: ActionPlan, values: FieldValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of plan.fields) {
    if (field.readOnly) continue;
    const value = coerce(field, (values[field.name] ?? "").trim());
    if (value !== undefined) payload[field.name] = value;
  }
  return withPublishedDiscriminator(plan, payload);
}

/**
 * Carry the ENGINE'S OWN discriminator into the payload, when it published one.
 *
 * WHY THIS EXISTS — a real defect this file had, found in a real browser
 * ----------------------------------------------------------------------
 * Every reviewed mutation this workbench sent was refused by the Engine with
 * `invalid-or-mismatched-diagram-command`, and the previous lane recorded that
 * as the Engine's verdict rather than as a bug in the request. It was a bug in
 * the request. The Engine's own command module discriminates on a `type`
 * member INSIDE the command — `parseDiagramCommand` reads `raw.type` and
 * refuses anything whose type does not match the operation — and this module
 * was dropping it, because the address field was classified as
 * "envelope-owned, so never sent".
 *
 * That classification was right for the ENVELOPE and wrong for the PAYLOAD.
 * The envelope's `type` is the address; the command's `type` is the
 * discriminant the Engine's validator reads, and the Engine publishes that
 * value in its own descriptor under the address field name — as a single-member
 * enumeration, or as a `const`, which is the same JSON Schema fact in the other
 * spelling. So the value is READ, not invented, and not typed by the user:
 *
 *   • it is only added when the descriptor publishes exactly one legal value
 *     for that property, so there is no choice to make;
 *   • it is never added when the user supplied a value of their own;
 *   • it is added to the PAYLOAD only. The envelope keeps its own address.
 *
 * This is not a compatibility shim and not a hard-coded operation name. It is
 * the published schema's own value, forwarded into the place the Engine's
 * validator reads it, and `tests/capabilityActionLoop.test.ts` asserts it
 * against an unseen fixture capability rather than a real one.
 */
function withPublishedDiscriminator(plan: ActionPlan, payload: Record<string, unknown>): Record<string, unknown> {
  const name = plan.transport.addressField;
  if (Object.prototype.hasOwnProperty.call(payload, name)) return payload;
  const published = plan.descriptor.requestSchema.properties?.[name];
  // Exactly one legal value is the Engine stating "this is the only value this
  // property can take". Zero publishes nothing to forward; two or more would be
  // a genuine choice, and a browser that picked one of them would be choosing the
  // Engine's vocabulary for the user.
  const only = published !== undefined && Array.isArray(published.enum) && published.enum.length === 1
    ? published.enum[0]
    : published !== undefined && typeof published.const === "string"
      ? published.const
      : undefined;
  if (typeof only !== "string" || only === "") return payload;
  return { ...payload, [name]: only };
}

// ---------------------------------------------------------------------------
// Preview / apply
// ---------------------------------------------------------------------------

/** Which published reviewed-mutation route a body is sent to. */
export interface ActionRequest {
  readonly phase: "preview" | "apply";
  readonly path: string;
  readonly body: unknown;
}

/**
 * Assemble the request for one phase.
 *
 * `read` is answered by the client directly; the reviewed phases go through the
 * change-set authority, where a mutation accumulates as working state and only
 * publication writes canonical state. The operation is wrapped in the
 * repository's own pinned `AuthoringRequest` contract rather than in a
 * hand-written body, so there is no second description of that shape here.
 */
export function buildRequest(
  plan: ActionPlan,
  values: FieldValues,
  options: { phase: "preview" | "apply"; projectId: string; changeSetId?: string; base?: { diagramId: string; revision: number } },
): ActionRequest {
  const route = plan.transport.paths[options.phase];
  if (route === "") throw new Error(`The ${plan.transport.transport} transport has no ${options.phase} route.`);
  const path = route
    .replace("{projectId}", encodeURIComponent(options.projectId))
    .replace("{changeSetId}", encodeURIComponent(options.changeSetId ?? ""));
  const payload = buildPayload(plan, values);
  const envelope: Record<string, unknown> = { [plan.transport.addressField]: plan.address };
  // The discriminator the payload carries must agree with the envelope's own
  // address, or the two would describe different operations. `addressValue`
  // reads exactly that published enumeration, so they cannot drift; the check
  // is here because a divergence would be silent and would be the Engine's
  // refusal to explain it.
  if (payload[plan.transport.addressField] !== undefined
    && payload[plan.transport.addressField] !== plan.address) {
    throw new Error(
      `The Engine's own schema describes ${plan.descriptor.id} with a discriminator that is not the address this `
      + `request carries. Nothing was sent: a command addressed one way and discriminated another is a request no `
      + "reviewer could interpret.",
    );
  }
  envelope[plan.transport.payloadField] = payload;
  for (const auxiliary of plan.transport.auxiliaryFields) {
    const value = payload[auxiliary];
    if (value !== undefined) envelope[auxiliary] = value;
  }
  const request: AuthoringRequest = {
    operations: [{ operationId: plan.operationId, type: plan.address, command: payload }],
    ...(options.base ? { base: options.base } : {}),
  };
  return { phase: options.phase, path, body: request };
}

/** The deterministic read body: the address plus the payload, un-wrapped. */
export function buildReadRequest(plan: ActionPlan, values: FieldValues): { path: string; body: unknown } {
  const route = plan.transport.paths.read;
  if (route === "") throw new Error(`The ${plan.transport.transport} transport has no read route.`);
  const payload = buildPayload(plan, values);
  const body: Record<string, unknown> = { [plan.transport.addressField]: plan.address };
  body[plan.transport.payloadField] = payload;
  for (const auxiliary of plan.transport.auxiliaryFields) {
    const value = payload[auxiliary];
    if (value !== undefined) body[auxiliary] = value;
  }
  return { path: route, body };
}

// ---------------------------------------------------------------------------
// Review / diagnostics
// ---------------------------------------------------------------------------

/**
 * Read the ACCEPTANCE the Engine's own result schema declares.
 *
 * The single required boolean is the acceptance flag, and it is looked up by
 * shape rather than by name: a result schema that requires a boolean gives the
 * client exactly one thing it may treat as "the Engine said yes". Anything
 * else — no declared acceptance, several declared booleans, no required
 * boolean — yields `undefined`, which the UI treats as NOT stageable.
 */
export function declaredAcceptance(descriptor: CapabilityDescriptor): string | undefined {
  const properties = propertiesOf(descriptor.resultSchema);
  const required = new Set(descriptor.resultSchema.required ?? []);
  const candidates = Object.keys(properties)
    .filter(name => properties[name]?.type === "boolean" && required.has(name));
  return candidates.length === 1 ? candidates[0] : undefined;
}

export interface Outcome {
  /** The raw Engine result, always retained. Nothing is filtered away. */
  readonly raw: unknown;
  /** The Engine's acceptance flag name, when it published one. */
  readonly acceptanceField: string | undefined;
  /** True only when the Engine published an acceptance flag AND it read true. */
  readonly accepted: boolean;
  /** Named refusal and warning lists, labelled with the Engine's own property names. */
  readonly diagnostics: readonly { field: string; entries: readonly string[] }[];
  /** A canonical change list, when the result schema declares one. */
  readonly diff: readonly { field: string; entries: readonly unknown[] }[];
}

function stringArrayAt(record: Record<string, unknown>, name: string): readonly string[] | undefined {
  const value = record[name];
  if (!Array.isArray(value)) return undefined;
  return value.filter((entry): entry is string => typeof entry === "string");
}

function arrayAt(record: Record<string, unknown>, name: string): readonly unknown[] | undefined {
  const value = record[name];
  return Array.isArray(value) ? value : undefined;
}

/**
 * Interpret a result against the descriptor that asked for it.
 *
 * `accepted` is the ONLY thing that may unlock a stage. When the Engine
 * published no acceptance flag this returns false, and the UI must then say the
 * outcome could not be read rather than offering a button on hope.
 */
export function readOutcome(descriptor: CapabilityDescriptor, raw: unknown): Outcome {
  const acceptanceField = declaredAcceptance(descriptor);
  const record = isRecord(raw) ? raw : {};
  const accepted = acceptanceField !== undefined && record[acceptanceField] === true;
  const properties = propertiesOf(descriptor.resultSchema);
  const diagnostics: { field: string; entries: readonly string[] }[] = [];
  const diff: { field: string; entries: readonly unknown[] }[] = [];
  for (const [name, schema] of Object.entries(properties)) {
    if (name === acceptanceField) continue;
    if (schema.type !== "array") continue;
    if (isRecord(schema.items) && schema.items.type === "string") {
      const entries = stringArrayAt(record, name);
      if (entries && entries.length > 0) diagnostics.push({ field: name, entries });
      continue;
    }
    const entries = arrayAt(record, name);
    if (entries && entries.length > 0) diff.push({ field: name, entries });
  }
  return { raw, acceptanceField, accepted, diagnostics, diff };
}

/** The result of a reviewed phase, as this repository's contract types it. */
export function asAuthoringResult(raw: unknown): AuthoringResult | undefined {
  return isRecord(raw) && typeof raw.ok === "boolean" ? (raw as unknown as AuthoringResult) : undefined;
}
